"use client";

export type PaginationProps = {
  total: number;
  perPage: number;
  page: number; // 1-based
  onPageChange: (page: number) => void;
  className?: string;
};

// Slice helper kept colocated so callers don't have to import a second
// utility — `paginate(rows, page, perPage)` returns the visible slice.
export function paginate<T>(rows: T[], page: number, perPage: number): T[] {
  const start = (page - 1) * perPage;
  return rows.slice(start, start + perPage);
}

export default function Pagination({
  total,
  perPage,
  page,
  onPageChange,
  className = "",
}: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / perPage));
  if (pageCount <= 1) return null;

  const clamped = Math.min(Math.max(1, page), pageCount);
  const start = total === 0 ? 0 : (clamped - 1) * perPage + 1;
  const end = Math.min(clamped * perPage, total);

  const btnBase =
    "px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

  return (
    <div className={`flex items-center justify-between gap-3 ${className}`}>
      <div className="text-xs text-slate-500 tabular-nums">
        {start}–{end} of {total}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(clamped - 1)}
          disabled={clamped === 1}
          className={`${btnBase} bg-white/5 border-white/10 text-slate-300 hover:bg-white/10`}
        >
          ← Prev
        </button>
        <span className="text-xs text-slate-400 px-2 tabular-nums">
          Page {clamped} / {pageCount}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(clamped + 1)}
          disabled={clamped === pageCount}
          className={`${btnBase} bg-white/5 border-white/10 text-slate-300 hover:bg-white/10`}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
